/**
 * Панель поиска и фильтров списка дел — `.filter-bar` макета (ui-blocks §7.3),
 * дополненная селектами ТЗ §8.4 (стадия, категория, приоритет, исполнитель,
 * отделение, источник, срок).
 *
 * Чип задаёт базовый набор параметров `GET /api/cases`, селекты его уточняют:
 * значение селекта всегда перекрывает то, что поставил чип.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaseListParams, DepartmentDto, Role, UserDto } from '@/api/types';
import { SOURCE_LABELS, STAGE_LABELS, STAGES } from '@/lib/stages';
import { categoryLabel } from '@/lib/categories';

export type ChipKey = 'open' | 'hot' | 'late' | 'p1' | 'repeat' | 'closed' | 'unassigned';

export const CHIP_LABELS: Readonly<Record<ChipKey, string>> = {
    open: 'Все открытые',
    hot: 'Горит',
    late: 'Просрочено',
    p1: 'Приоритет 1',
    repeat: 'Повторные',
    closed: 'Закрытые',
    unassigned: 'Без исполнителя',
};

/** Чипы экрана «Список дел» (ТЗ §8.4). */
export const CASES_CHIPS: readonly ChipKey[] = ['open', 'hot', 'late', 'p1', 'repeat', 'closed', 'unassigned'];

/** Чипы блока «Мои дела» — шесть кнопок макета. */
export const MY_CHIPS: readonly ChipKey[] = ['open', 'hot', 'late', 'p1', 'repeat', 'closed'];

/**
 * 14 категорий классификатора — копия `CATEGORIES` из `apps/api/src/domain/categoryMatrix.ts`.
 * Отдельной ручки со справочником категорий контракт не даёт, поэтому список зашит здесь.
 */
export const CATEGORY_NAMES: readonly string[] = [
    'Запрос меддокументов',
    'Исправление медицинских документов',
    'Качество медпомощи',
    'Этика и деонтология',
    'ЧП / смерть / экстренная помощь',
    'Организация и доступность медпомощи',
    'Условия пребывания и хозяйственные вопросы',
    'Разъяснение порядка',
    'Лекарственное обеспечение и льготы',
    'Нарушение порядка рассмотрения предыдущего обращения',
    'Претензия / досудебное требование',
    'Предоставление сведений третьим лицам',
    'Благодарность',
    'Иное — требует ручной квалификации',
];

const DEADLINE_OPTIONS: readonly { value: string; label: string }[] = [
    { value: 'late', label: 'Просрочено' },
    { value: 'soon', label: 'Горит (≤ 3 дней)' },
    { value: 'today', label: 'Срок сегодня' },
    { value: 'week', label: 'Срок на неделе' },
    { value: 'ok', label: 'Срок в норме' },
];

/** Состояние фильтров; пустая строка = «не выбрано». */
export interface FilterState {
    chip: ChipKey;
    q: string;
    stage: string;
    category: string;
    priority: string;
    executor: string;
    department: string;
    source: string;
    deadline: string;
}

export const DEFAULT_FILTERS: FilterState = {
    chip: 'open',
    q: '',
    stage: '',
    category: '',
    priority: '',
    executor: '',
    department: '',
    source: '',
    deadline: '',
};

/** Параметры запроса, которые задаёт сам чип. */
export function chipParams(chip: ChipKey): CaseListParams {
    switch (chip) {
        case 'hot':
            return { stageGroup: 'open', deadline: 'soon' };
        case 'late':
            return { stageGroup: 'open', deadline: 'late' };
        case 'p1':
            return { stageGroup: 'open', priority: 1 };
        case 'closed':
            return { stageGroup: 'closed' };
        case 'unassigned':
            return { stageGroup: 'open', executor: 'none' };
        case 'repeat':
        case 'open':
        default:
            return { stageGroup: 'open' };
    }
}

export interface CaseFiltersProps {
    value: FilterState;
    onChange: (patch: Partial<FilterState>) => void;
    chips: readonly ChipKey[];
    /** Селекты ТЗ §8.4; в блоке «Мои дела» их нет — там только поиск и чипы. */
    showSelects?: boolean;
    executors?: readonly UserDto[];
    departments?: readonly DepartmentDto[];
    role?: Role;
    meId?: string;
}

export function CaseFilters({
    value,
    onChange,
    chips,
    showSelects = false,
    executors,
    departments,
    role,
    meId,
}: CaseFiltersProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState(value.q);
    const pushed = useRef(value.q);

    // Внешняя смена поиска (переход по ссылке, кнопка «назад») подхватывается полем.
    useEffect(() => {
        if (value.q !== pushed.current) {
            pushed.current = value.q;
            setText(value.q);
        }
    }, [value.q]);

    // Ввод отдаём наверх с задержкой 250 мс, чтобы не дёргать сервер на каждую букву.
    useEffect(() => {
        if (text === pushed.current) return;
        const timer = window.setTimeout(() => {
            pushed.current = text;
            onChange({ q: text });
        }, 250);
        return () => window.clearTimeout(timer);
    }, [text, onChange]);

    const clearSearch = useCallback(() => {
        pushed.current = '';
        setText('');
        onChange({ q: '' });
        inputRef.current?.focus();
    }, [onChange]);

    return (
        <div className="filter-bar">
            <input
                ref={inputRef}
                type="search"
                className="search-input"
                placeholder="Номер дела, фамилия заявителя или тема"
                aria-label="Поиск по делам"
                value={text}
                onChange={(event) => setText(event.target.value)}
            />

            <span className="seg" role="group" aria-label="Фильтр дел">
                {chips.map((chip) => (
                    <button
                        key={chip}
                        type="button"
                        data-filter={chip}
                        className={value.chip === chip ? 'active' : undefined}
                        aria-pressed={value.chip === chip}
                        onClick={() => onChange({ chip })}
                    >
                        {CHIP_LABELS[chip]}
                    </button>
                ))}
            </span>

            {showSelects ? (
                <>
                    <select
                        className="act-select"
                        aria-label="Стадия"
                        value={value.stage}
                        onChange={(event) => onChange({ stage: event.target.value })}
                    >
                        <option value="">Все стадии</option>
                        {STAGES.map((stage) => (
                            <option key={stage} value={stage}>
                                {STAGE_LABELS[stage]}
                            </option>
                        ))}
                    </select>

                    <select
                        className="act-select"
                        aria-label="Категория"
                        value={value.category}
                        onChange={(event) => onChange({ category: event.target.value })}
                    >
                        <option value="">Все категории</option>
                        {CATEGORY_NAMES.map((name) => (
                            <option key={name} value={name}>
                                {categoryLabel(name)}
                            </option>
                        ))}
                    </select>

                    <select
                        className="act-select"
                        aria-label="Приоритет"
                        value={value.priority}
                        onChange={(event) => onChange({ priority: event.target.value })}
                    >
                        <option value="">Любой приоритет</option>
                        {[1, 2, 3, 4, 5].map((priority) => (
                            <option key={priority} value={String(priority)}>
                                Приоритет {priority}
                            </option>
                        ))}
                    </select>

                    <select
                        className="act-select"
                        aria-label="Исполнитель"
                        value={value.executor}
                        onChange={(event) => onChange({ executor: event.target.value })}
                    >
                        <option value="">Все исполнители</option>
                        <option value="none">Без исполнителя</option>
                        {role === 'executor' && meId ? <option value={meId}>Только мои</option> : null}
                        {(executors ?? []).map((executor) => (
                            <option key={executor.id} value={executor.id}>
                                {executor.full_name}
                            </option>
                        ))}
                    </select>

                    {/* Справочник отделений отдаёт только `/api/settings/departments`, а он закрыт
                        ролью `chief`. Без списка селект умеет ровно «все» и «без отделения» —
                        то есть не фильтрует; в таком виде его не показываем вовсе. */}
                    {(departments ?? []).length > 0 || value.department !== '' ? (
                        <select
                            className="act-select"
                            aria-label="Отделение"
                            value={value.department}
                            onChange={(event) => onChange({ department: event.target.value })}
                        >
                            <option value="">Все отделения</option>
                            <option value="none">Без отделения</option>
                            {(departments ?? []).map((department) => (
                                <option key={department.id} value={department.id}>
                                    {department.name}
                                </option>
                            ))}
                            {/* Отделение пришло ссылкой, а справочника нет: даём его снять. */}
                            {value.department !== '' &&
                            value.department !== 'none' &&
                            !(departments ?? []).some((item) => item.id === value.department) ? (
                                <option value={value.department}>Выбранное отделение</option>
                            ) : null}
                        </select>
                    ) : null}

                    <select
                        className="act-select"
                        aria-label="Источник"
                        value={value.source}
                        onChange={(event) => onChange({ source: event.target.value })}
                    >
                        <option value="">Все источники</option>
                        {Object.entries(SOURCE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>
                                {label}
                            </option>
                        ))}
                    </select>

                    <select
                        className="act-select"
                        aria-label="Срок ответа"
                        value={value.deadline}
                        onChange={(event) => onChange({ deadline: event.target.value })}
                    >
                        <option value="">Любой срок</option>
                        {DEADLINE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </>
            ) : null}

            <button type="button" className="search-clear" hidden={text.length === 0} onClick={clearSearch}>
                Сбросить
            </button>
        </div>
    );
}

export default CaseFilters;
